import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from "react";

interface Props {
  /** Contenu du bouton déclencheur. */
  trigger: ReactNode;
  children: ReactNode;
  triggerClassName?: string;
  align?: "left" | "right";
  /** Ferme le popover quand le contenu change d'avis (contrôle externe optionnel). */
  onOpenChange?: (open: boolean) => void;
}

/** Popover ancré au trigger — fermeture Escape / clic extérieur. */
export function Popover({
  trigger,
  children,
  triggerClassName,
  align = "left",
  onOpenChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const set = (v: boolean) => {
    setOpen(v);
    onOpenChange?.(v);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && set(false);
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) set(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const panelStyle: CSSProperties =
    align === "right" ? { right: 0 } : { left: 0 };

  return (
    <div className="popover-root" ref={rootRef}>
      <button
        type="button"
        className={triggerClassName ?? "btn"}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => set(!open)}
      >
        {trigger}
      </button>
      {open && (
        <div className="popover-panel" style={panelStyle} role="dialog">
          {children}
        </div>
      )}
    </div>
  );
}
